/**
 * AppCard.jsx
 * One app: its visits, its downloads, its pages, its links.
 *
 * Everything in this card belongs to the one app named at the top of it. That
 * is the whole point of the card — the screen it replaced added five products
 * across three sites into a single "157 page views", which answered no question
 * anybody actually had.
 *
 * Each app is also scaled against itself. A bar here is full when it is this
 * app's best day, not the busiest app's best day, so a small product still has
 * a shape worth reading.
 *
 * The whole card folds, and so does each part of it, because five apps' worth
 * of charts and page lists is a great deal of scrolling to reach the third one.
 * What stays out in the open is what a decision is made on: the three figures
 * and anything that needs attention. What folds away is detail. Shut, the card
 * still carries its downloads, its visits and a count of anything wrong, so a
 * screen of closed apps is the comparison rather than a row of mysteries.
 *
 * Dependencies: Bars.jsx, DayChart.jsx, Fold.jsx, components/Icons.jsx,
 * utils/time.js
 */

import { useState } from 'react';
import { Download, Eye, Plus, Warning } from '../Icons.jsx';
import { formatDay, formatLondon } from '../../utils/time.js';
import { Bars, List, Stats, n } from './Bars.jsx';
import DayChart from './DayChart.jsx';
import { AppFold, Fold } from './Fold.jsx';

/** What to say under a download's name: this window against all time. */
function linkNote(link) {
  if (link.clicksAllTime === 0) {
    return link.match_url
      ? 'Never counted once — check the button on the site'
      : 'Never counted once — the tracked address may not be on the site yet';
  }
  const parts = [`${n(link.clicksAllTime)} all time`];
  if (link.last_click_at) parts.push(`last ${formatLondon(link.last_click_at)}`);
  return parts.join(' · ');
}

/**
 * The things that explain a zero, which is the figure people misread.
 *
 * A zero download can mean nobody wanted the app — or that nobody was counting,
 * or that the button on the site points somewhere this has never heard of.
 * Those are completely different problems and the card says which it is.
 */
function warningsFor(app, from) {
  const out = [];

  if (!app.counting.everCounted) {
    out.push({
      tone: 'danger',
      text:
        `Nothing on ${app.host} has ever been counted. Until the one line of script is on its ` +
        'pages (it is at the bottom of this screen), every figure here stays at zero however ' +
        'many people visit.',
    });
  } else if (app.counting.firstDay && app.counting.firstDay > from) {
    out.push({
      tone: 'warn',
      text:
        `Counting on ${app.host} began on ${formatDay(app.counting.firstDay)}. Anything before ` +
        'that is blank because nobody was counting, not because nobody came.',
    });
  }

  const never = app.links.filter((l) => !l.archived && l.clicksAllTime === 0);
  if (never.length > 0 && app.counting.everCounted) {
    out.push({
      tone: 'warn',
      text:
        `Not one tap has ever been counted on ${never.map((l) => l.label).join(', ')}. Either the ` +
        'button on the site points somewhere else, or "counts clicks on" does not match the ' +
        'address it already uses.',
    });
  }

  if (!app.enabled && app.listed) {
    out.push({ tone: 'warn', text: `${app.host} is paused, so nothing new is being counted.` });
  }

  if (!app.listed) {
    out.push({
      tone: 'warn',
      text: `${app.host} is no longer in your list of sites. These are the figures it left behind.`,
    });
  }

  return out;
}

export default function AppCard({ app, from, to, busy, defaultOpen, onCopy, onArchive, onAdd }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [match, setMatch] = useState('');

  const warnings = warningsFor(app, from);
  // A retired link with nothing in the window is history; one with downloads in
  // it is part of this month's total and has to be shown or the sum is wrong.
  const counted = app.links.filter((l) => !l.archived || l.clicks > 0);
  const live = app.links.filter((l) => !l.archived);

  return (
    <section className="card app-card">
      <AppFold
        defaultOpen={defaultOpen}
        icon={
          <span className={`tile tile--sm ${app.totals.downloads > 0 ? 'tile--ok' : ''}`}>
            <Download size={17} />
          </span>
        }
        name={app.name}
        host={
          app.brandName && app.brandName !== app.name
            ? `${app.host} · ${app.brandName}`
            : app.host
        }
        /* A shut app still has to say how it did, or opening all of them to
           compare is the only way to read the screen. */
        meta={
          <>
            <span className="fold__figure">
              <b>{n(app.totals.downloads)}</b> downloads
            </span>
            <span className="fold__figure">
              <b>{n(app.totals.views)}</b> views
            </span>
            {warnings.length > 0 && (
              <span className="badge badge--warn">
                {warnings.length} to check
              </span>
            )}
            <span className={`badge ${app.enabled ? 'badge--ok' : 'badge--warn'}`}>
              {app.listed ? (app.enabled ? 'Counting' : 'Paused') : 'Removed'}
            </span>
          </>
        }
      >
        <Stats
          items={[
            { label: 'Downloads', value: app.totals.downloads, sub: `of ${app.name}` },
            { label: 'Page views', value: app.totals.views, sub: `on ${app.host}` },
            {
              label: 'Arrived directly',
              value: app.totals.direct,
              sub: 'typed it or used a bookmark',
            },
          ]}
        />

        {warnings.map((w) => (
          <div className={`notice notice--${w.tone === 'danger' ? 'error' : 'info'}`} key={w.text}>
            <Warning />
            <span>{w.text}</span>
          </div>
        ))}

        {/* Open to begin with: the shape of the last few weeks, and which of this
            app's downloads people actually took, are what the screen is for. The
            long lists below them are not, so they start shut. */}
        <Fold title="Day by day" note={`${app.totals.downloads} downloads · ${app.totals.views} visits`} defaultOpen>
          <div className="app-charts">
            <div className="app-chart">
              <h3>
                <Download size={15} /> Downloads of {app.name}
              </h3>
              <DayChart
                rows={app.downloadsByDay.map((d) => ({ day: d.day, value: d.clicks }))}
                from={from}
                to={to}
                since={app.counting.firstDay}
                tone="downloads"
                unit="downloads"
              />
            </div>

            <div className="app-chart">
              <h3>
                <Eye size={15} /> Visits to {app.host}
              </h3>
              <DayChart
                rows={app.viewsByDay.map((d) => ({ day: d.day, value: d.views }))}
                from={from}
                to={to}
                since={app.counting.firstDay}
                tone="visits"
                unit="visits"
              />
            </div>
          </div>
        </Fold>

        <Fold
          title="Which of these downloads people took"
          note={`${counted.length} ${counted.length === 1 ? 'download' : 'downloads'}`}
          defaultOpen
        >
          <Bars
            rows={counted.map((l) => ({
              key: l.id,
              label: `${l.label}${l.archived ? ' · retired' : ''}`,
              sub: linkNote(l),
              value: l.clicks,
              tone: 'downloads',
            }))}
            empty={`No downloads are set up for ${app.name} yet. Add one below.`}
          />
        </Fold>

        <Fold title={`Most visited pages on ${app.host}`} note={`${app.topPages.length} pages`}>
          <List
            rows={app.topPages.map((p) => ({ key: p.path, label: p.path, value: p.views }))}
            empty="No pages counted in this window."
          />
        </Fold>

        <Fold
          title="Where its visitors came from"
          note={app.topReferrers.length > 0 ? `${app.topReferrers.length} sites` : 'all direct'}
        >
          <List
            rows={app.topReferrers.map((r) => ({
              key: r.referrer,
              label: r.referrer,
              value: r.views,
            }))}
            empty="Everyone arrived directly, or from a page on the site itself."
          />
        </Fold>

        {/* The setup for this app sits with this app's figures, shut, because it
            is read once and the figures are read every week. */}
        <Fold
          title={`Download links for ${app.name}`}
          note={`${live.length} set up`}
        >
          <div className="rows">
            {live.length === 0 && <p className="row__sub">None yet.</p>}
            {live.map((l) => (
              <div className="row" key={l.id}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="row__label">{l.label}</div>
                  <div className="row__sub mono">{l.tracked_url}</div>
                  <div className="row__sub">
                    goes to {l.url}
                    {l.match_url ? ` · counts clicks on ${l.match_url}` : ''}
                  </div>
                </div>
                <div className="row__end" style={{ gap: 12 }}>
                  <button className="btn btn--sm" onClick={() => onCopy(l.tracked_url)}>
                    Copy
                  </button>
                  <button
                    className="btn btn--sm btn--danger"
                    disabled={busy}
                    onClick={() => onArchive(l)}
                  >
                    Retire
                  </button>
                </div>
              </div>
            ))}
          </div>

          {adding ? (
            <div className="inline-form">
              <div className="field">
                <label htmlFor={`lbl-${app.id}`}>Name</label>
                <input
                  id={`lbl-${app.id}`}
                  placeholder={`${app.name} for Windows`}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={`url-${app.id}`}>Where it goes</label>
                <input
                  id={`url-${app.id}`}
                  placeholder={`https://${app.host}/download/windows`}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={`mtc-${app.id}`}>Counts clicks on</label>
                <input
                  id={`mtc-${app.id}`}
                  placeholder={`${app.host}/download/windows`}
                  value={match}
                  onChange={(e) => setMatch(e.target.value)}
                />
                <span className="hint">
                  A button already on the site counts without being changed, as long as this matches
                  the address it points at — including a checkout address such as buy.stripe.com/…
                </span>
              </div>
              <button
                className="btn btn--primary"
                disabled={busy || !label.trim() || !url.trim()}
                onClick={async () => {
                  await onAdd({ label, url, match, siteId: app.id, brandId: app.brandId });
                  setLabel('');
                  setUrl('');
                  setMatch('');
                  setAdding(false);
                }}
              >
                Add to {app.name}
              </button>
              <button className="btn" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="btn btn--sm"
              style={{ marginTop: 12 }}
              disabled={!app.listed}
              onClick={() => setAdding(true)}
            >
              <Plus size={14} /> Add a download to {app.name}
            </button>
          )}
        </Fold>
      </AppFold>
    </section>
  );
}
